import type { FileUIPart } from "@intelligems/ig-types";
import { Message, MessageContent } from "@src/components/ai-elements/message";
import { useExecutionPanelSlotProps } from "@src/features/ai/components/WorkflowEditor/execution/panel/ExecutionPanelContext";

import { UserAttachmentGroup as UserAttachmentGroupType } from "@src/features/ai/components/WorkflowEditor/execution/types";
import { UploadedFilesCard } from "@src/features/ai/components/WorkflowEditor/execution/upload/UploadedFilesCard";

interface UserAttachmentGroupProps {
  item: UserAttachmentGroupType;
}

/**
 * Renders a user turn that carried file attachments — optionally
 * alongside the user's text. The text bubble and the summary card live
 * inside one `<Message>` wrapper so they read as one turn with no
 * inter-`<Message>` gap to manage. When the user sent files only, the
 * `<MessageContent>` is skipped and the wrapper just contains the card —
 * which means the surrounding `<Message>`'s default top/bottom padding
 * cleanly separates this turn from the assistant's reply.
 *
 * The files themselves render as the Figma "File uploaded" summary card:
 * one full-width card listing every file on the turn by name, sitting
 * below the (right-aligned) text bubble.
 */
export const UserAttachmentGroup = ({ item }: UserAttachmentGroupProps) => {
  const getSlotProps = useExecutionPanelSlotProps();
  const { parts, textPart } = item;
  if (parts.length === 0) {
    return null;
  }

  const text =
    textPart && textPart.part.type === "text" ? textPart.part.text : "";

  const filenames = parts.map(
    (flatPart) => (flatPart.part as FileUIPart).filename ?? "(unnamed)"
  );

  return (
    <Message from="user">
      <div className="flex w-full flex-col items-end gap-2">
        {text && (
          <MessageContent className={getSlotProps("userMessage")?.className}>
            {text}
          </MessageContent>
        )}
        <UploadedFilesCard filenames={filenames} />
      </div>
    </Message>
  );
};
